import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { createPortal } from 'react-dom';

/**
 * Single-select dark dropdown with a year/options grid (no multiselect, nav arrows, or Apply footer).
 */
const DropdownYearGridDark = ({
  options = [],
  selectedValue = '',
  onSelect = () => {},
  placeholder = 'Year',
  width = 'w-28',
  disabled = false,
  usePortal = false,
  portalRef = null,
  gridCols = 'grid-cols-3',
  dropdownClassMenu = 'bg-gray-700 shadow-lg ring-1 ring-black ring-opacity-5',
  dropdownClassEmpty = 'px-3 py-1.5 text-xs text-gray-300 italic',
  triggerBgOpacity = 30,
}) => {
  const opacity = typeof triggerBgOpacity === 'number' ? Math.min(100, Math.max(0, triggerBgOpacity)) / 100 : 0.3;
  const [isOpen, setIsOpen] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const triggerBgStyle = { backgroundColor: `rgb(107 114 128 / ${isHover ? 1 : opacity})` };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (buttonRef.current && buttonRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handlePick = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  const getPortalStyle = () => {
    const ref = portalRef?.current ? portalRef : buttonRef;
    if (!ref.current) return {};
    const rect = ref.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 8,
      minWidth: Math.max(rect.width, 160),
    };
  };

  const getButtonLabel = () => {
    if (selectedValue === '' || selectedValue == null) return placeholder;
    const found = options.find(
      (opt) => String(typeof opt === 'object' ? opt.value : opt) === String(selectedValue)
    );
    if (found && typeof found === 'object' && found.label != null) return found.label;
    return String(selectedValue);
  };

  const menuContent = (
    <div
      ref={dropdownRef}
      className={`rounded-md ${dropdownClassMenu}`}
      style={
        usePortal
          ? {}
          : { position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', minWidth: '10rem' }
      }
    >
      <div className={`grid ${gridCols} gap-2 p-2`}>
        {options.map((option, index) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          const isSelected = String(selectedValue) === String(optionValue);
          return (
            <button
              key={`${optionValue}-${index}`}
              type="button"
              onClick={() => handlePick(optionValue)}
              className={`py-2 rounded-md text-xs font-medium transition-all border min-w-0 ${
                isSelected
                  ? 'bg-green-600 text-white border-green-400'
                  : 'bg-gray-600 text-gray-200 border-transparent hover:bg-gray-500'
              } focus:outline-none`}
            >
              {optionLabel}
            </button>
          );
        })}
      </div>
      {options.length === 0 && <div className={dropdownClassEmpty}>No options available</div>}
    </div>
  );

  return (
    <div className={`relative ${width}`} ref={buttonRef}>
      <button
        type="button"
        onClick={handleToggle}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        disabled={disabled}
        style={disabled ? undefined : triggerBgStyle}
        className={`inline-flex w-full justify-between items-center gap-1 rounded-lg border-0 px-2.5 py-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-white focus:ring-opacity-30 ${
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'cursor-pointer'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate tabular-nums">{getButtonLabel()}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-white opacity-70 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen &&
        (usePortal
          ? createPortal(<div style={getPortalStyle()}>{menuContent}</div>, document.body)
          : menuContent)}
    </div>
  );
};

export default DropdownYearGridDark;
